// What was said in calls and meetings: the list, one transcript read back as a conversation, and a search across all
// of them. Searching is the point of keeping them, so it is the first thing on the page rather than a tab elsewhere.
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatDuration } from "../../lib/calls";
import { formatDay, formatTime } from "../../lib/chat";
import { Alert, Badge, Card, Field, Spinner } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";
import { MakeTaskFromLine } from "../projects/from-transcript";

type Summary = {
  id: string; title: string; status: "listening" | "processing" | "ready" | "failed";
  startedAt: string; durationSeconds: number | null; wordCount: number; meetingId: string | null;
};
type Line = { id: string; speakerId: string | null; speakerName: string; startMs: number; endMs: number; body: string };
type Hit = { transcriptId: string; title: string; startedAt: string; speakerName: string; startMs: number; headline: string };

const STATUS: Record<Summary["status"], { text: string; tone: "slate" | "brand" | "amber" | "red" }> = {
  listening: { text: "Listening", tone: "red" }, processing: { text: "Finishing…", tone: "amber" },
  ready: { text: "Ready", tone: "brand" }, failed: { text: "Failed", tone: "red" },
};

/** mm:ss from the start of the call, so a line can be found in the recording. */
export const atTime = (ms: number) => {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// The search service marks matches with << >>, which is turned into something readable rather than shown raw.
function Marked({ text }: { text: string }) {
  return (
    <>
      {text.split(/<<|>>/).map((part, i) => (
        i % 2 === 1 ? <mark key={i} className="rounded bg-amber-200 px-0.5 dark:bg-amber-500/40 dark:text-amber-50">{part}</mark> : <span key={i}>{part}</span>
      ))}
    </>
  );
}

export function TranscriptsPage() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const asked = query.trim();
  const list = useQuery({
    queryKey: ["transcripts", org.slug],
    queryFn: () => api<{ transcripts: Summary[] }>(`/orgs/${org.slug}/transcripts`),
  });
  const found = useQuery({
    queryKey: ["transcripts", org.slug, "search", asked],
    queryFn: () => api<{ results: Hit[] }>(`/orgs/${org.slug}/transcripts/search?q=${encodeURIComponent(asked)}`),
    enabled: asked.length > 1,
  });

  async function remove(t: Summary) {
    if (!confirm(`Delete the transcript of “${t.title}”? What was said in it is deleted with it.`)) return;
    try {
      await api(`/orgs/${org.slug}/transcripts/${t.id}`, { method: "DELETE" });
      await qc.invalidateQueries({ queryKey: ["transcripts", org.slug] });
    } catch (err) { setError(errorText(err)); }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Transcripts</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          What was said in calls and meetings that were written down. They are kept as long as recordings are, under the
          organization's rule, and can be deleted sooner.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card className="mb-6">
        <Field label="Search everything that was said" name="q" placeholder="A word or a name" value={query}
          onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
        {asked.length > 1 && (
          <div className="mt-4">
            {found.isLoading && <div className="flex justify-center p-4 text-brand-600"><Spinner /></div>}
            {found.data && found.data.results.length === 0 && <p className="text-sm text-slate-500">Nothing said in any meeting matches “{asked}”.</p>}
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {(found.data?.results ?? []).map((hit, i) => (
                <li key={`${hit.transcriptId}-${hit.startMs}-${i}`} className="py-3">
                  <Link to={`/o/${org.slug}/transcripts/${hit.transcriptId}`} className="block rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <p className="text-sm"><span className="font-medium">{hit.speakerName}</span> <span className="text-slate-400">· {atTime(hit.startMs)}</span></p>
                    <p className="mt-0.5 text-sm text-slate-700 dark:text-slate-300"><Marked text={hit.headline} /></p>
                    <p className="mt-0.5 text-xs text-slate-500">{hit.title} · {formatDay(hit.startedAt)}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {list.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
      {list.data && list.data.transcripts.length === 0 && (
        <Card>
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="file" /></div>
            <p className="font-medium">No transcripts yet</p>
            <p className="mt-1 text-sm text-slate-500">Turn on the transcript in a call or meeting and what is said will be written down here.</p>
          </div>
        </Card>
      )}
      {(list.data?.transcripts.length ?? 0) > 0 && (
        <Card className="p-0">
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {(list.data?.transcripts ?? []).map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="file" /></span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    <span className="min-w-0 break-words">{t.title}</span>
                    {t.status !== "ready" && <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].text}</Badge>}
                  </p>
                  <p className="text-sm text-slate-500">
                    {formatDay(t.startedAt)} at {formatTime(t.startedAt)}
                    {t.durationSeconds ? ` · ${formatDuration(t.durationSeconds)}` : ""}
                    {t.wordCount ? ` · ${t.wordCount.toLocaleString()} words` : ""}
                  </p>
                </div>
                <div className="flex w-full shrink-0 flex-wrap gap-1 sm:w-auto">
                  <Link to={`/o/${org.slug}/transcripts/${t.id}`} className="rounded-lg px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-700/30">Read</Link>
                  {t.status !== "listening" && (
                    <button type="button" onClick={() => remove(t)} className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30">Delete</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export function TranscriptPage() {
  const { org } = useOrg();
  const { transcriptId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["transcript", org.slug, transcriptId],
    queryFn: () => api<{ transcript: Summary; lines: Line[] }>(`/orgs/${org.slug}/transcripts/${transcriptId}`),
    // While it is still being written, new lines keep arriving.
    refetchInterval: (q) => (q.state.data?.transcript.status === "listening" ? 4000 : false),
  });
  // A link to one line (#line-<id>, from a task made of it): bring it into view.
  const { hash } = useLocation();
  const loaded = Boolean(data);
  useEffect(() => {
    if (loaded && hash.startsWith("#line-")) document.getElementById(hash.slice(1))?.scrollIntoView({ block: "center" });
  }, [loaded, hash]);

  if (isLoading) return <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>;
  if (error || !data) return <div className="mx-auto max-w-3xl"><Alert>That transcript isn't available.</Alert></div>;

  const { transcript, lines } = data;
  const download = () => {
    const text = lines.map((l) => `[${atTime(l.startMs)}] ${l.speakerName}: ${l.body}`).join("\n");
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${transcript.title.replace(/[^\w -]+/g, "")}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Link to={`/o/${org.slug}/transcripts`} className="text-sm text-brand-700 hover:underline dark:text-brand-100">← All transcripts</Link>
          <h1 className="mt-1 break-words text-2xl font-semibold tracking-tight">{transcript.title}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {formatDay(transcript.startedAt)} at {formatTime(transcript.startedAt)}
            {transcript.durationSeconds ? ` · ${formatDuration(transcript.durationSeconds)}` : ""}
            {transcript.wordCount ? ` · ${transcript.wordCount.toLocaleString()} words` : ""}
          </p>
        </div>
        {lines.length > 0 && (
          <button type="button" onClick={download} className="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-700/30">
            <Icon name="download" className="mr-1 inline h-4 w-4" />Download
          </button>
        )}
      </div>

      {transcript.status === "listening" && <Alert kind="info">This call is still going. Lines appear as people speak.</Alert>}
      {transcript.status === "failed" && <Alert>Something went wrong while writing this one down.</Alert>}

      <Card className="mt-4">
        {lines.length === 0 && <p className="text-sm text-slate-500">Nothing has been said yet.</p>}
        <ol className="space-y-3">
          {lines.map((l) => (
            <li key={l.id} id={`line-${l.id}`} className={`group flex gap-3 rounded-lg ${hash === `#line-${l.id}` ? "-mx-2 bg-amber-50 px-2 py-1 dark:bg-amber-900/20" : ""}`}>
              <span className="w-12 shrink-0 pt-0.5 text-right font-mono text-xs text-slate-400">{atTime(l.startMs)}</span>
              <p className="min-w-0 flex-1 text-sm leading-relaxed">
                <span className="font-semibold">{l.speakerName}:</span> <span className="text-slate-700 dark:text-slate-300">{l.body}</span>
              </p>
              {transcript.status !== "listening" && <MakeTaskFromLine transcriptId={transcript.id} line={l} at={atTime(l.startMs)} />}
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}
